import { Slider as SliderPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from './cn';

type SliderThumbProps = Omit<
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Thumb>,
  'children' | 'className'
>;

export interface SliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  trackClassName?: string;
  rangeClassName?: string;
  thumbClassName?: string;
  thumbProps?: SliderThumbProps;
}

const Slider = React.forwardRef<React.ComponentRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    {
      className,
      trackClassName,
      rangeClassName,
      thumbClassName,
      thumbProps,
      value,
      defaultValue,
      min = 0,
      max = 100,
      ...props
    },
    ref,
  ) => {
    const thumbCount = (value ?? defaultValue ?? [min]).length;

    return (
      <SliderPrimitive.Root
        ref={ref}
        data-slot="slider"
        value={value}
        defaultValue={defaultValue}
        min={min}
        max={max}
        className={cn(
          'relative flex w-full touch-none items-center select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
          className,
        )}
        {...props}
      >
        <SliderPrimitive.Track
          data-slot="slider-track"
          className={cn(
            'relative h-1.5 w-full grow overflow-hidden rounded-full bg-muted',
            trackClassName,
          )}
        >
          <SliderPrimitive.Range
            data-slot="slider-range"
            className={cn('absolute h-full bg-primary', rangeClassName)}
          />
        </SliderPrimitive.Track>
        {Array.from({ length: thumbCount }, (_, index) => (
          <SliderPrimitive.Thumb
            key={index}
            data-slot="slider-thumb"
            {...thumbProps}
            className={cn(
              'block size-3 shrink-0 rounded-full border border-primary bg-background shadow-sm transition-[color,box-shadow,opacity] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[disabled]:pointer-events-none',
              thumbClassName,
            )}
          />
        ))}
      </SliderPrimitive.Root>
    );
  },
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
